// booking-form-utils.ts
import { format, parse } from 'date-fns';
import type {
  BookingFormValues,
  ActivityFormValues,
  FnbFormValues,
  ActivityAddonFormValues,
  SmartPricingData,
} from './booking-form.types';

import { round2 } from '@/features/console/pages/fee-settings/fee-settings.utils';
import {
  hasTieredPricing,
  linesWithPeople,
  storedActivityTotal,
  type IPricedFormActivity,
  type IStoredAgeGroupLine,
} from './booking-age-groups';
import { chargedTotal } from './booking-price-variant';
import {
  isBookableInParallel,
  isBookableInSingle,
} from '@/features/inventory/inventory.constants';

export const formatTime = (timeString: string) => {
  try {
    // Try parsing as HH:mm:ss first, then fall back to HH:mm
    let time;
    if (timeString.includes(':') && timeString.split(':').length === 3) {
      // Format: HH:mm:ss
      time = parse(timeString, 'HH:mm:ss', new Date());
    } else {
      // Format: HH:mm
      time = parse(timeString, 'HH:mm', new Date());
    }
    return format(time, 'HH:mm');
  } catch {
    return timeString?.substring(0, 5) || ''; // fallback to first 5 characters
  }
};

// Strip the protocol (http/https) and any trailing slash for display purposes
export const formatDomainDisplay = (domain?: string | null): string => {
  if (!domain) return '';
  return domain.replace(/^https?:\/\//i, '').replace(/\/+$/, '');
};

// Helper function to calculate end time
export const calculateEndTime = (
  startTime: string,
  durationInMinutes: number
) => {
  try {
    // Try parsing as HH:mm:ss first, then fall back to HH:mm
    let time;
    if (startTime.includes(':') && startTime.split(':').length === 3) {
      // Format: HH:mm:ss
      time = parse(startTime, 'HH:mm:ss', new Date());
    } else {
      // Format: HH:mm
      time = parse(startTime, 'HH:mm', new Date());
    }
    // Add duration in minutes (not hours)
    const endTime = new Date(time.getTime() + durationInMinutes * 60 * 1000);
    return format(endTime, 'HH:mm');
  } catch {
    return '';
  }
};

export const safeAccess = <T = unknown>(
  obj: unknown,
  path: string,
  defaultValue: T = null as T
): T => {
  return path.split('.').reduce<unknown>((current, key) => {
    if (
      current !== null &&
      current !== undefined &&
      typeof current === 'object' &&
      key in current
    ) {
      return (current as Record<string, unknown>)[key];
    }
    return defaultValue;
  }, obj) as T;
};

/**
 * What smart pricing and combi deals have taken off the order, summed across
 * activities, their add-ons and F&B.
 *
 * Display only: each item's `price` already carries its reduced rate, so the
 * subtotal reflects this discount and it must NOT be subtracted again.
 * `basePrice` holds the original; the difference is the saving.
 */
export const calculateSmartPricingDiscount = (
  watchedValues: BookingFormValues
): number => {
  let totalDiscount = 0;

  const saving = (
    basePrice: number,
    currentPrice: number,
    priceVariant: string | null | undefined,
    participantCount: number
  ): number =>
    // A surcharge is not a saving: a line smart pricing raised counts as 0,
    // never as a negative that shrinks the other lines' discount.
    Math.max(
      0,
      chargedTotal(basePrice - currentPrice, participantCount, priceVariant)
    );

  watchedValues.activities?.forEach(activity => {
    // Age group and group size lines have no single list price to compare.
    if (hasTieredPricing(activity as IPricedFormActivity)) return;
    totalDiscount += saving(
      activity.basePrice || 0,
      activity.price || 0,
      activity.priceVariant,
      Number(activity.participantCount) || 0
    );

    activity.addons?.forEach(addon => {
      totalDiscount += saving(
        addon?.basePrice || addon?.price || 0,
        addon?.price || 0,
        addon?.priceVariant,
        Number(addon?.participantCount ?? 0) || 0
      );
    });
  });

  watchedValues.foodAndBeverages?.forEach(fnb => {
    totalDiscount += saving(
      fnb.basePrice || 0,
      fnb.price || 0,
      fnb.priceVariant,
      Number(fnb.participantCount ?? 0) || 0
    );
  });

  return totalDiscount;
};

export const calculateOrderDetails = (watchedValues: BookingFormValues) => {
  const orderItems: Array<{
    name: string;
    price: number;
    participantCount: number;
    totalPrice: number;
    type: 'activity' | 'addon' | 'fnb';
    taxPercentage: number;
    isDiscounted?: boolean;
    originalPrice?: number;
    priceVariant?: string;
    /** Age group lines with people, for an activity priced per age group. */
    ageGroups?: IStoredAgeGroupLine[];
    /** Priced by age group or group size: no single unit price to quote. */
    tiered?: boolean;
    /** The group size tier that applies, e.g. "5–10 participants". */
    groupSizeLabel?: string | null;
  }> = [];

  let subtotal = 0;
  let totalTax = 0;
  const totalDiscount = 0;

  // Process Activities
  if (watchedValues?.activities && Array.isArray(watchedValues.activities)) {
    watchedValues.activities.forEach((activity: ActivityFormValues) => {
      if (activity?.name && activity?.price && activity?.participantCount) {
        const price = Number(activity.price) || 0;
        const participants = Number(activity.participantCount) || 0;
        const taxPercentage = Number(activity.taxActivity) || 0;
        const priceVariant = String(activity.priceVariant || '').toLowerCase();

        // Price is tax-inclusive, so totalPrice already contains tax. Age
        // group and group size pricing land in `totalPrice` on the form.
        const totalPrice = storedActivityTotal(activity);
        // Extract tax from inclusive price: tax = price * rate / (100 + rate)
        const taxAmount =
          taxPercentage > 0
            ? (totalPrice * taxPercentage) / (100 + taxPercentage)
            : 0;

        orderItems.push({
          name: activity.name,
          price,
          participantCount: participants,
          totalPrice,
          type: 'activity',
          taxPercentage,
          priceVariant,
          ageGroups: linesWithPeople(activity.ageGroups),
          tiered: hasTieredPricing(activity as IPricedFormActivity),
          groupSizeLabel: activity.groupSizeLabel ?? null,
        });

        subtotal += totalPrice;
        totalTax += taxAmount;

        // Process Add-ons for this activity
        if (activity.addons && Array.isArray(activity.addons)) {
          activity.addons.forEach((addon: ActivityAddonFormValues) => {
            if (addon?.name && addon?.price && addon?.participantCount) {
              const addonPrice = Number(addon.price) || 0;
              const addonParticipants = Number(addon.participantCount) || 0;
              const addonTaxPercentage = Number(addon.taxAddons) || 0;
              const addonPriceVariant = String(
                addon.priceVariant || ''
              ).toLowerCase();

              const addonTotalPrice = chargedTotal(
                addonPrice,
                addonParticipants,
                addon.priceVariant
              );
              const addonTaxAmount =
                addonTaxPercentage > 0
                  ? (addonTotalPrice * addonTaxPercentage) /
                    (100 + addonTaxPercentage)
                  : 0;

              orderItems.push({
                name: `${addon.name}`,
                price: addonPrice,
                participantCount: addonParticipants,
                totalPrice: addonTotalPrice,
                type: 'addon',
                taxPercentage: addonTaxPercentage,
                priceVariant: addonPriceVariant,
              });

              subtotal += addonTotalPrice;
              totalTax += addonTaxAmount;
            }
          });
        }
      }
    });
  }

  // Process Food & Beverages
  if (
    watchedValues?.foodAndBeverages &&
    Array.isArray(watchedValues.foodAndBeverages)
  ) {
    watchedValues.foodAndBeverages.forEach((fnb: FnbFormValues) => {
      if (fnb?.name && fnb?.price && fnb?.participantCount) {
        const price = Number(fnb.price) || 0;
        const participants = Number(fnb.participantCount) || 0;
        const taxPercentage = Number(fnb.taxFnB) || 0;
        const priceVariant = String(fnb.priceVariant || '').toLowerCase();

        const totalPrice = chargedTotal(price, participants, fnb.priceVariant);

        const taxAmount =
          taxPercentage > 0
            ? (totalPrice * taxPercentage) / (100 + taxPercentage)
            : 0;

        orderItems.push({
          name: fnb.name,
          price,
          participantCount: participants,
          totalPrice,
          type: 'fnb',
          taxPercentage,
          priceVariant,
        });

        subtotal += totalPrice;
        totalTax += taxAmount;
      }
    });
  }

  // Price is tax-inclusive, so subtotal already contains tax
  // Final total = subtotal - discount (no need to add tax)
  const finalTotal = subtotal - totalDiscount;

  return {
    orderItems,
    subtotal,
    totalTax,
    totalDiscount,
    finalTotal,
  };
};

/** Fixed VAT rate applied to the administration fee (percent). */
export const FEE_VAT_RATE = 21;

/**
 * The administration-fee basis: a VAT-inclusive net total (after discount) with
 * a flat 21% VAT stripped off — the same method as the fee-settings page.
 * Floored at 0 so a €0 booking yields a €0 basis.
 */
export const feeBasisFromInclTotal = (inclTotal: number): number =>
  Math.max(0, inclTotal) / (1 + FEE_VAT_RATE / 100);

/** The 21% VAT portion of an excl-VAT fee. */
export const feeVatOf = (feeExcl: number): number =>
  round2(feeExcl * (FEE_VAT_RATE / 100));

/** An excl-VAT fee grossed up to include its 21% VAT. */
export const feeInclVat = (feeExcl: number): number =>
  round2(feeExcl * (1 + FEE_VAT_RATE / 100));

/**
 * Converts a stored administration fee back to excl-VAT. The API grosses the
 * fee up to incl-VAT on save, so `booking.administrationFee` comes back incl —
 * divide by 1.21 to recover the excl value the UI/PDF work with. Floored at 0.
 */
export const feeExclFromStored = (feeIncl: number): number =>
  round2(Math.max(0, feeIncl) / (1 + FEE_VAT_RATE / 100));

export const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
  }).format(amount);
};

/** A VAT-inclusive line amount with its own tax rate (percentage). */
export interface TaxableItem {
  priceIncl: number;
  taxRate: number;
}

/**
 * Splits a set of VAT-inclusive line amounts into incl/tax/excl totals using
 * each item's own tax rate, grouping by rate and rounding per group — the same
 * method the booking summary / edit page use, so results match the backend.
 */
export const calcExclVatTotal = (
  items: TaxableItem[]
): { inclTotal: number; taxTotal: number; exclTotal: number } => {
  let inclTotal = 0;
  const groups = new Map<number, number>();
  items.forEach(({ priceIncl, taxRate }) => {
    inclTotal += priceIncl;
    if (taxRate > 0) {
      const tax = (priceIncl * taxRate) / (100 + taxRate);
      groups.set(taxRate, (groups.get(taxRate) ?? 0) + tax);
    }
  });
  let taxTotal = 0;
  groups.forEach(amount => {
    taxTotal += Math.round(amount * 100) / 100;
  });
  return { inclTotal, taxTotal, exclTotal: inclTotal - taxTotal };
};

/**
 * Builds the VAT-inclusive line items (activities + addons + F&B) from the form
 * values. `resolveActivityPrice` supplies the effective unit price for an
 * activity (e.g. combi-deal price in edit mode).
 */
export const buildTaxableItems = (
  watched: BookingFormValues,
  resolveActivityPrice: (activity: ActivityFormValues, index: number) => number
): TaxableItem[] => {
  const items: TaxableItem[] = [];

  watched.activities?.forEach((activity: ActivityFormValues, index: number) => {
    if (activity?.name && activity?.price && activity?.participantCount) {
      const unit = resolveActivityPrice(activity, index);
      const participants = Number(activity.participantCount) || 0;
      items.push({
        priceIncl: hasTieredPricing(activity as IPricedFormActivity)
          ? storedActivityTotal(activity)
          : chargedTotal(unit, participants, activity.priceVariant),
        taxRate: Number(activity.taxActivity) || 0,
      });
      activity.addons?.forEach((addon: ActivityAddonFormValues) => {
        if (addon?.name && addon?.price && addon?.participantCount) {
          const addonUnit = Number(addon.price) || 0;
          const addonParticipants = Number(addon.participantCount) || 0;
          items.push({
            priceIncl: chargedTotal(
              addonUnit,
              addonParticipants,
              addon.priceVariant
            ),
            taxRate: Number(addon.taxAddons) || 0,
          });
        }
      });
    }
  });

  watched.foodAndBeverages?.forEach((fnb: FnbFormValues) => {
    if (fnb?.name && fnb?.price && fnb?.participantCount) {
      const unit = Number(fnb.price) || 0;
      const participants = Number(fnb.participantCount) || 0;
      items.push({
        priceIncl: chargedTotal(unit, participants, fnb.priceVariant),
        taxRate: Number(fnb.taxFnB) || 0,
      });
    }
  });

  return items;
};

/**
 * A combi deal's discount: a percentage off the partner activity's rate, or a
 * fixed euro amount off it. A bare number is a percentage.
 */
export type CombiDealDiscount =
  | number
  | { value: number; type: 'Percentage' | 'Fixed' };

export const combiDealDiscountValue = (discount?: CombiDealDiscount): number =>
  (typeof discount === 'object' ? discount.value : discount) ?? 0;

/**
 * A combi deal's discount, taken off a price that has already been smart-priced.
 *
 * The deal is stored as a percentage or a fixed amount off the partner
 * activity's rate, and the result is money, so it is rounded to whole cents and
 * never goes below zero. One implementation for every caller (the start-time
 * grid and the day builder's locked lanes), because copies of this arithmetic
 * drift apart.
 */
export const applyCombiDealDiscount = (
  price: number,
  discount?: CombiDealDiscount
): number => {
  const value = combiDealDiscountValue(discount);
  if (value <= 0) return price;

  const discountAmount =
    typeof discount === 'object' && discount.type === 'Fixed'
      ? value
      : price * (value / 100);
  return Math.round(Math.max(0, price - discountAmount) * 100) / 100;
};

export const calculateSmartPriceForTime = (
  originalTime: string,
  basePrice: number,
  smartPricingData: SmartPricingData[],
  bookingDate: string
) => {
  if (
    !smartPricingData ||
    !Array.isArray(smartPricingData) ||
    smartPricingData.length === 0
  ) {
    return { smartPrice: basePrice, discount: 0 };
  }

  if (!bookingDate || !basePrice) {
    return { smartPrice: basePrice, discount: 0 };
  }

  // Get day name from booking date
  const date = new Date(bookingDate);
  const dayNames = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];
  const dayName = dayNames[date.getDay()];

  // Helper function to convert time string to minutes
  const timeToMinutes = (timeStr: string): number => {
    if (!timeStr) return 0;
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Sort schedules by priority: date-specific schedules first, then default schedules
  const sortedSchedules = [...smartPricingData].sort(
    (a: SmartPricingData, b: SmartPricingData) => {
      const aHasDateRange = a['startDate'] && a['endDate'];
      const bHasDateRange = b['startDate'] && b['endDate'];

      if (aHasDateRange && !bHasDateRange) return -1;
      if (!aHasDateRange && bHasDateRange) return 1;
      return 0;
    }
  );

  // Find the appropriate schedule based on booking date
  let selectedScheduleItem: SmartPricingData | null = null;

  for (const scheduleItem of sortedSchedules) {
    if (scheduleItem['startDate'] && scheduleItem['endDate']) {
      const startDate = new Date(scheduleItem['startDate'] as string);
      const endDate = new Date(scheduleItem['endDate'] as string);
      const bookingDateObj = new Date(bookingDate);

      if (bookingDateObj >= startDate && bookingDateObj <= endDate) {
        selectedScheduleItem = scheduleItem;
        break;
      }
    } else {
      if (!selectedScheduleItem) {
        selectedScheduleItem = scheduleItem;
      }
    }
  }

  if (!selectedScheduleItem?.['schedule']) {
    return { smartPrice: basePrice, discount: 0 };
  }

  // Find the schedule for the specific day
  const daySchedule = (
    selectedScheduleItem['schedule'] as Array<Record<string, unknown>>
  ).find((schedule: Record<string, unknown>) => schedule['day'] === dayName);

  if (!daySchedule || !(daySchedule['pricing'] as unknown[])?.length) {
    return { smartPrice: basePrice, discount: 0 };
  }

  // Check if the original time falls within any pricing period
  const originalTimeMinutes = timeToMinutes(originalTime);

  for (const pricing of daySchedule['pricing'] as Array<
    Record<string, unknown>
  >) {
    const startMinutes = timeToMinutes(pricing['start'] as string);
    const endMinutes = timeToMinutes(pricing['end'] as string);

    const adjustedEndMinutes =
      endMinutes < startMinutes ? endMinutes + 24 * 60 : endMinutes;

    if (
      originalTimeMinutes >= startMinutes &&
      originalTimeMinutes <= adjustedEndMinutes
    ) {
      const discountValue = parseFloat(pricing['value'] as string);
      let smartPrice = basePrice;

      if (selectedScheduleItem['discountType'] === 'Percentage') {
        smartPrice = basePrice * (1 + discountValue / 100);
      } else if (selectedScheduleItem['discountType'] === 'Fixed') {
        smartPrice = basePrice + discountValue;
      }

      smartPrice = Math.max(0, smartPrice);

      return {
        smartPrice: Math.round(smartPrice * 100) / 100,
        discount: discountValue,
      };
    }
  }

  return { smartPrice: basePrice, discount: 0 };
};

/**
 * A booking is reopenable when it is cancelled or expired and has not yet
 * been reopened in the current session. Drives the read-only overview and
 * the "Reopen Booking" button.
 */
export const isBookingReopenable = (
  bookingStatus: string | null | undefined,
  rebookingCompleted: boolean
): boolean =>
  (bookingStatus === 'cancel' || bookingStatus === 'expired') &&
  !rebookingCompleted;

/**
 * A save that moves the booking INTO `cancel`: the only save that asks whether
 * to email the customer. An already cancelled booking edited again, and a
 * reopen (always back to `active`), never ask; the API sends nothing for them.
 */
export const isCancelTransition = (
  loadedStatus: string | null | undefined,
  savedStatus: string | null | undefined
): boolean => loadedStatus !== 'cancel' && savedStatus === 'cancel';

/**
 * Timeslot capacity is computed fresh (like create mode, no add-back of the
 * booking's own participants) for cancelled, expired, and draft bookings.
 */
export const usesFreshCapacity = (
  bookingStatus: string | null | undefined
): boolean =>
  bookingStatus === 'cancel' ||
  bookingStatus === 'expired' ||
  bookingStatus === 'draft';

/**
 * The "pay by invoice" toggle is editable on any booking that has no recorded
 * payment yet — including the reopen flow, which is just one such case. Once
 * money has been received the payment type is settled, so the toggle is
 * read-only.
 */
export const canEditInvoiceToggle = (grossPaidFromDetails: number): boolean =>
  grossPaidFromDetails <= 0.01;

/**
 * Whether the "Add Payment" action is available. Cases:
 * - the pre-existing partially-paid case;
 * - reopened bookings that still have an outstanding balance;
 * - by-invoice bookings with an outstanding balance — payment on an invoice
 *   booking is recorded manually via Add Payment (there is no payment-status
 *   dropdown), so the action must appear even when the booking is Not Paid.
 */
export const canAddPaymentOnBooking = (params: {
  paymentStatus: string | null | undefined;
  totalAmount: number;
  totalPaidFromDetails: number;
  isReopenContext: boolean;
  isByInvoice: boolean;
}): boolean => {
  const {
    paymentStatus,
    totalAmount,
    totalPaidFromDetails,
    isReopenContext,
    isByInvoice,
  } = params;
  const outstanding = totalAmount - totalPaidFromDetails;
  const isFullyPaid = outstanding <= 0.01 && totalAmount > 0;
  const isPartiallyPaid = paymentStatus?.toLowerCase() === 'partially paid';

  if (isPartiallyPaid && !isFullyPaid) return true;
  if (isReopenContext && outstanding > 0.01) return true;
  if (isByInvoice && outstanding > 0.01) return true;
  return false;
};

/**
 * What the manual discount takes off, in euros: a percentage of the booking
 * value or a fixed amount. This is the stored `discount`; `discountValue` is
 * what was typed, in `discountType`'s unit (10% of 41.60 is stored as
 * discountValue 10, discount 4.16).
 */
export const calculateManualDiscount = (
  bookingValue: number,
  discountType: string | null | undefined,
  discountValue: number
): number =>
  discountType === 'Percentage'
    ? (bookingValue * discountValue) / 100
    : discountValue;

/**
 * Recomputes the persisted booking total from the current schedule, mirroring
 * the save transform (booking.hooks.ts): (subtotal incl. tax − discount) plus
 * the administration fee incl. its 21% VAT. Used when the schedule is edited
 * or a booking is reopened so the header total matches the pricing panel and
 * the amount that will be saved — the admin fee must NOT be dropped.
 *
 * `subtotalInclTax` is the sum of item totals (prices are tax-inclusive);
 * `adminFeeExclVat` is the excl-VAT fee amount (already parsed by the caller).
 */
export const calculateScheduleTotal = (params: {
  subtotalInclTax: number;
  discountType: string | null | undefined;
  discountValue: number;
  adminFeeEnabled: boolean;
  adminFeeExclVat: number;
}): number => {
  const {
    subtotalInclTax,
    discountType,
    discountValue,
    adminFeeEnabled,
    adminFeeExclVat,
  } = params;

  // A percentage discount is charged over the whole booking value, never over
  // the balance due — payments already recorded do not shrink it (ELI-223).
  // A fixed discount is an absolute amount.
  const discountAmount =
    discountType === 'Percentage'
      ? (subtotalInclTax * discountValue) / 100
      : discountValue;

  const subtotalAfterDiscount = subtotalInclTax - discountAmount;
  const fee = adminFeeEnabled ? feeInclVat(adminFeeExclVat) : 0;
  return subtotalAfterDiscount + fee;
};

/**
 * How the activity being booked sits in the day: alongside another activity,
 * or in a time slot of its own.
 */
export type ActivityPlacement = 'parallel' | 'standalone';

/**
 * The advisory for booking a product into a placement its CMS "Booking Option"
 * does not cover, or undefined when the two agree.
 *
 * A warning rather than a filter on purpose: the rule is enforced on the
 * customer-facing widget, and the CMS stays flexible so staff can book an
 * exception by hand. The copy therefore says what the setting is and what the
 * widget would do, rather than telling the operator to stop.
 *
 * Says nothing when `bookingOption` is missing, for the same reason the gates
 * fail open: it is not guaranteed on a list payload, and an absent value is
 * "not configured", not "misconfigured".
 */
export const bookingOptionWarning = (
  bookingOption: string | null | undefined,
  placement: ActivityPlacement
): string | undefined => {
  if (placement === 'parallel') {
    if (isBookableInParallel(bookingOption)) return undefined;
    return 'This activity is set to Standalone, so the widget will not offer it alongside another activity. You can still book it here.';
  }

  if (isBookableInSingle(bookingOption)) return undefined;
  return 'This activity is set to Parallel, so the widget will only offer it alongside another activity. You can still book it here.';
};

/** Which of the two code endpoints a typed code belongs to. */
export type BookingCodeKind = 'coupon' | 'giftCard';

/**
 * Which endpoint should quote a code the agent typed into the one code field.
 *
 * `GIFT-` is the only reliable marker, and deliberately so: a gift card number
 * is always system-generated (`generateGiftCardCode`), never typed by staff, so
 * every card carries the prefix. A coupon code is entered by hand in the CMS —
 * now required to start `COUP-`, but older records need not — so "coupon" is the
 * fallback rather than a second prefix match. That keeps an unprefixed legacy
 * code reaching the endpoint that can still answer for it, instead of
 * dead-ending in the field.
 *
 * The dash is part of the marker. `GIFTDEAL10` is a plausible coupon code and
 * must not be mistaken for stored value.
 */
export const classifyBookingCode = (code: string): BookingCodeKind =>
  code.trim().toUpperCase().startsWith('GIFT-') ? 'giftCard' : 'coupon';

/** An option in the booking form's "Group" select. */
export type GroupTypeOption = { value: string; label: string };

/**
 * The options the "Group" select should offer when editing a booking.
 *
 * Group types are CMS records now, so a booking saved before the list existed
 * — or under a type since renamed or deactivated — holds a `group` string that
 * matches no option. FormSelect would render an empty select over a value that
 * is still in the form and still required: the field would look unanswered
 * while quietly submitting the old value on save.
 *
 * Appending the saved value keeps the select faithful to what is stored, and
 * picking anything else replaces it. The extra option goes last so it does not
 * displace a current type from where staff expect to find it.
 */
export const mergeSavedGroupOption = (
  options: GroupTypeOption[],
  savedGroup?: string | null
): GroupTypeOption[] => {
  if (!savedGroup) return options;
  if (options.some(option => option.value === savedGroup)) return options;

  /**
   * Bookings store the group lowercased (see `toGroupPayload`), while the
   * options carry the type's display name. A case-only difference is the same
   * type, so that option takes the stored value instead of a duplicate being
   * appended.
   */
  const normalized = savedGroup.trim().toLowerCase();
  const caseMatch = options.findIndex(
    option => option.value.trim().toLowerCase() === normalized
  );
  if (caseMatch !== -1) {
    return options.map((option, index) =>
      index === caseMatch ? { ...option, value: savedGroup } : option
    );
  }

  return [...options, { value: savedGroup, label: savedGroup }];
};

/**
 * The `group` / `groupId` pair a booking payload carries.
 *
 * `group` is sent lowercased so every booking stores one spelling of a type,
 * whatever case it was named with in Group Type Setting. `groupId` is the
 * type's record id, matched by name; it is omitted for a value no current type
 * carries (a legacy or since-renamed group kept on an edited booking).
 */
export const toGroupPayload = (
  group: string | null | undefined,
  groupTypes: { id?: string; name?: string }[] = []
): { group?: string | null; groupId?: string } => {
  const normalized = group?.trim().toLowerCase();
  if (!normalized) return { group };

  const groupId = groupTypes.find(
    groupType => groupType.name?.trim().toLowerCase() === normalized
  )?.id;

  return { group: normalized, ...(groupId ? { groupId } : {}) };
};

/**
 * Staff and the client test the booking flow on the live system, under names
 * like "Test", "Testing" or "Jan Tester". Those bookings belong in the `test`
 * status, out of the reporting and the planboard, without anyone having to
 * remember to switch it.
 *
 * A word has to *start* with "test", so "Test", "testing" and "test123" count
 * while a name that merely contains the letters ("Contesse") does not.
 */
const TEST_NAME_PATTERN = /(?:^|[^\p{L}])test/iu;

export const isTestBookingName = (
  firstName?: string | null,
  lastName?: string | null
): boolean =>
  [firstName, lastName].some(name => TEST_NAME_PATTERN.test(name ?? ''));

/**
 * The status a new booking is created with: `test` for a test name, otherwise
 * whatever the form carries.
 */
export const resolveCreateBookingStatus = <T extends string | null | undefined>(
  bookingStatus: T,
  firstName?: string | null,
  lastName?: string | null
): T | 'test' =>
  isTestBookingName(firstName, lastName) ? 'test' : bookingStatus;
